import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Sparkles, ChevronRight, X, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import JeaButton from "@/components/jea/JeaButton";
import BrokerResultCard from "@/components/queries/BrokerResultCard";
import ConsolidatedAssetSummary from "@/components/queries/ConsolidatedAssetSummary";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate } from "@/lib/dates";
import { BROKER_SLUGS_ORDERED } from "@/lib/queries";
import { TIER_MONTHS } from "@/lib/pricing";

/**
 * Post-query reschedule prompt (bottom-right toast-style card).
 * Only surfaces when the run was manual and the debtor's stored next_query_at
 * is still in the future.
 */
const ReschedulePrompt = ({ debtorId, runId, oldDate, newDate, onDismiss }) => {
  const [busy, setBusy] = useState(false);

  const persistDismiss = async () => {
    try {
      await apiClient.patch(`/queries/${runId}`, {
        reschedule_prompt_dismissed_at: new Date().toISOString(),
      });
    } catch { /* non-blocking */ }
  };

  const reschedule = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/debtors/${debtorId}`, { next_query_at: newDate });
      await persistDismiss();
      toast.success(`Next scheduled query updated to ${shortDate(newDate)}.`);
      onDismiss(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const keepOriginal = async () => {
    await persistDismiss();
    onDismiss(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 40, y: 20 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      exit={{ opacity: 0, x: 40, y: 20 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
      className="fixed bottom-6 right-6 z-[70] w-[440px] max-w-[calc(100vw-3rem)]"
      data-testid="reschedule-prompt"
    >
      <div
        className="bg-bg-secondary rounded-[12px] p-6 shadow-elevated"
        style={{ border: "1px solid var(--color-accent-border-40)" }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
               style={{ background: "var(--color-accent-bg-15)", border: "1px solid var(--color-accent-border-40)" }}>
            <CalendarClock size={18} style={{ color: "var(--color-accent)" }} />
          </div>
          <button
            type="button"
            onClick={() => onDismiss(false)}
            className="p-1 text-text-secondary hover:text-white"
            data-testid="reschedule-close"
          >
            <X size={14} />
          </button>
        </div>
        <h3 className="text-white font-bold text-[18px] leading-tight">
          You just ran a manual query
        </h3>
        <p className="text-text-secondary text-[14px] mt-2 leading-5">
          Your next scheduled query was set for <span className="jea-date">{shortDate(oldDate)}</span>.
          Reschedule it based on today's manual run
          {" — new date "}<span className="jea-date">{shortDate(newDate)}</span>?
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={keepOriginal}
            disabled={busy}
            className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[13px] font-semibold transition-colors disabled:opacity-40"
            data-testid="reschedule-keep-original"
          >
            Keep Original Schedule
          </button>
          <JeaButton size="sm" onClick={reschedule} loading={busy} data-testid="reschedule-confirm">
            Reschedule
          </JeaButton>
        </div>
      </div>
    </motion.div>
  );
};

const QueryResultsPage = () => {
  const { id: debtorId, run_id: runId } = useParams();
  const nav = useNavigate();
  const [run, setRun] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});
  const [showReschedule, setShowReschedule] = useState(false);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/queries/${runId}`);
        if (!alive) return;
        setRun(data.query_run);
        setReport(data.report);

        // Stagger reveal
        const brokers = [...(data.query_run.broker_results || [])].sort(
          (a, b) => (a.latency_ms || 0) - (b.latency_ms || 0)
        );
        let base = 100;
        brokers.forEach((b, i) => {
          const delay = Math.min(200 + i * 180, 1400);
          setTimeout(() => {
            if (alive) setRevealed((r) => ({ ...r, [b.broker_slug]: true }));
          }, base + delay);
        });

        // Reschedule prompt — surface once all cards have finished revealing.
        // Only if there's a previous_next_query_at in the future AND user
        // hasn't dismissed the prompt on a previous visit.
        const oldDate = data.query_run.previous_next_query_at;
        const alreadyDismissed = !!data.query_run.reschedule_prompt_dismissed_at;
        if (oldDate && !alreadyDismissed) {
          const oldTs = new Date(oldDate).getTime();
          if (!Number.isNaN(oldTs) && oldTs > Date.now()) {
            const totalReveal = base + Math.min(200 + brokers.length * 180, 1400) + 400;
            setTimeout(() => {
              if (alive) setShowReschedule(true);
            }, totalReveal);
          }
        }
      } catch (e) {
        toast.error(formatApiError(e));
        nav(`/debtors/${debtorId}?tab=query-history`, { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Auto-dismiss after 30s of no interaction
  useEffect(() => {
    if (!showReschedule) return;
    dismissTimerRef.current = setTimeout(() => setShowReschedule(false), 30000);
    return () => clearTimeout(dismissTimerRef.current);
  }, [showReschedule]);

  const orderedResults = useMemo(() => {
    if (!run?.broker_results) return [];
    const bySlug = Object.fromEntries(run.broker_results.map((r) => [r.broker_slug, r]));
    return BROKER_SLUGS_ORDERED.filter((s) => bySlug[s]).map((s) => bySlug[s]);
  }, [run]);

  const rescheduledDate = useMemo(() => {
    if (!run) return null;
    const tier = run.debtor_snapshot?.tier || "bronze";
    const months = TIER_MONTHS[tier] || 12;
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toISOString();
  }, [run]);

  if (loading || !run) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-text-secondary" data-testid="query-results-loading">
        Loading query results…
      </div>
    );
  }

  const snap = run.debtor_snapshot || {};

  return (
    <div className="space-y-6" data-testid="query-results-page">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <nav className="flex items-center gap-1 text-[13px] text-text-secondary">
            <button onClick={() => nav("/dashboard")} className="hover:text-accent-pink transition-colors" data-testid="qr-crumb-dashboard">
              Dashboard
            </button>
            <ChevronRight size={12} className="text-border-default" />
            <button onClick={() => nav(`/debtors/${debtorId}`)} className="hover:text-accent-pink transition-colors" data-testid="qr-crumb-debtor">
              {snap.full_name}
            </button>
            <ChevronRight size={12} className="text-border-default" />
            <span className="text-white">Query Results</span>
          </nav>
          <h1 className="text-white font-extrabold text-[28px] tracking-[-0.02em]">
            Query for <span className="text-accent-pink">{snap.full_name}</span>
          </h1>
          <div className="text-text-secondary text-[13px]">
            <span className="jea-date">{shortDate(run.completed_at || run.started_at)}</span>
            <span> · </span>
            {run.broker_slugs?.length} broker(s)
            <span> · Total </span>
            <span className="jea-mono text-accent-pink font-bold">{centsToUSD(run.total_cost_cents)}</span>
            {run.discount_cents > 0 && (
              <span className="text-success"> (saved {centsToUSD(run.discount_cents)})</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end shrink-0">
          {report?.id && (
            <JeaButton
              variant="outline-pink"
              className="gap-2"
              onClick={() => nav(`/debtors/${debtorId}/reports/${report.id}`)}
              data-testid="qr-view-report"
            >
              <FileText size={14} /> View Full Report
            </JeaButton>
          )}
          <button
            type="button"
            onClick={() => nav(`/debtors/${debtorId}?tab=query-history`)}
            className="text-text-secondary hover:text-white text-[13px] font-semibold inline-flex items-center gap-1"
            data-testid="qr-back-history"
          >
            <ArrowLeft size={12} /> Back to Query History
          </button>
        </div>
      </div>

      {report && (
        <ConsolidatedAssetSummary
          summary={report.summary}
          consolidated={report.consolidated_assets}
        />
      )}

      <div className="grid grid-cols-2 gap-4" data-testid="qr-broker-grid">
        {orderedResults.map((r, i) => {
          const isRevealed = !!revealed[r.broker_slug];
          return (
            <div key={r.broker_slug || i} data-testid={`qr-broker-slot-${r.broker_slug}`}>
              {isRevealed ? (
                <BrokerResultCard result={r} index={i} />
              ) : (
                <BrokerResultCard pending index={i} result={{ broker_slug: r.broker_slug }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Static footer prompt (extra affordance) */}
      <div className="bg-bg-secondary border border-accent-pink/30 rounded-[12px] p-6 flex items-center gap-4"
           data-testid="qr-reschedule-footer">
        <div className="w-10 h-10 rounded-full bg-accent-pink/15 border border-accent-pink/40 flex items-center justify-center shrink-0">
          <Sparkles size={16} className="text-accent-pink" />
        </div>
        <div className="flex-1">
          <div className="text-white font-semibold text-[15px]">Query complete — report ready.</div>
          <div className="text-text-secondary text-[13px] mt-1">
            Open the full report for the recommended enforcement path, or bump {snap.full_name}'s tier for more frequent monitoring.
          </div>
        </div>
        <JeaButton
          onClick={() => nav(`/debtors/${debtorId}?tab=overview`)}
          variant="outline-pink"
          className="gap-2"
          data-testid="qr-goto-overview"
        >
          Debtor Overview
        </JeaButton>
      </div>

      {/* Post-query reschedule prompt (bottom-right toast) */}
      <AnimatePresence>
        {showReschedule && rescheduledDate && (
          <ReschedulePrompt
            debtorId={debtorId}
            runId={runId}
            oldDate={run.previous_next_query_at}
            newDate={rescheduledDate}
            onDismiss={() => setShowReschedule(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default QueryResultsPage;
